import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { recordConsent } from "@/lib/visitor-tracking.functions";

const CONSENT_KEY = "axc-cookie-consent-v1";
const VISITOR_KEY = "axc-visitor-id";

export type CookieConsent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
};

export function getStoredConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookieConsent;
  } catch {
    return null;
  }
}

export function getOrCreateVisitorId(): string {
  if (typeof window === "undefined") return crypto.randomUUID();
  let id = window.localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = crypto.randomUUID();
    window.localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

/**
 * Programmatically re-open the cookie consent banner. Used by the
 * "Cookie-indstillinger" link in the footer so visitors can change their
 * choice after their initial decision.
 */
export function openCookieSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("axc:open-cookie-settings"));
}

export function CookieConsentBanner() {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);
  const recordConsentFn = useServerFn(recordConsent);

  useEffect(() => {
    const existing = getStoredConsent();
    if (!existing) {
      // Delay slightly to avoid flash on first paint
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, []);

  // Allow other parts of the UI (e.g. footer link) to re-open the banner
  // and pre-fill the toggles with the visitor's existing choices.
  useEffect(() => {
    const handler = () => {
      const existing = getStoredConsent();
      if (existing) {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
      setShowDetails(true);
      setOpen(true);
    };
    window.addEventListener("axc:open-cookie-settings", handler);
    return () => window.removeEventListener("axc:open-cookie-settings", handler);
  }, []);


  const persist = async (consent: { analytics: boolean; marketing: boolean }) => {
    const visitorId = getOrCreateVisitorId();
    const payload: CookieConsent = {
      necessary: true,
      analytics: consent.analytics,
      marketing: consent.marketing,
      decidedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
    setOpen(false);

    try {
      await recordConsentFn({
        data: {
          visitorId,
          consent: { necessary: true, analytics: consent.analytics, marketing: consent.marketing },
        },
      });
    } catch (e) {
      console.error("Failed to record consent on server", e);
    }

    // Notify other components (e.g. visitor tracking hook)
    window.dispatchEvent(new CustomEvent("axc:consent-changed", { detail: payload }));
  };

  if (!open) return null;

  // Bottom padding reserves 5rem on small screens for the mobile sticky CTA
  // bar on product pages (which is `md:hidden`). On md+ the sticky bar
  // disappears, so we drop back to a normal padding.
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:p-6 md:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-card text-card-foreground shadow-2xl">
        <div className="flex items-start gap-4 p-5 sm:p-6">
          <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
            <Cookie size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold">Cookies & privatliv</h2>
              <button
                onClick={() => persist({ analytics: false, marketing: false })}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Afvis alle"
              >
                <X size={18} />
              </button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Vi bruger cookies til at forbedre din oplevelse, måle trafik og levere relevant
              markedsføring. Du kan tilpasse dit samtykke nedenfor — eller acceptere alt.
            </p>

            {showDetails && (
              <div className="mt-4 space-y-3 rounded-lg border border-border bg-muted/40 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">Nødvendige</p>
                    <p className="text-xs text-muted-foreground">
                      Kræves for at hjemmesiden fungerer (login, formular-sikring).
                    </p>
                  </div>
                  <Switch checked disabled />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">Statistik</p>
                    <p className="text-xs text-muted-foreground">
                      Hjælper os med at se hvilke biler og sider der er populære.
                    </p>
                  </div>
                  <Switch checked={analytics} onCheckedChange={setAnalytics} />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">Markedsføring</p>
                    <p className="text-xs text-muted-foreground">
                      Bruges til retargeting på Facebook og Google, så vi kan vise dig relevante
                      biler igen.
                    </p>
                  </div>
                  <Switch checked={marketing} onCheckedChange={setMarketing} />
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                onClick={() => persist({ analytics: true, marketing: true })}
                className="bg-accent text-accent-foreground hover:brightness-110"
              >
                Accepter alle
              </Button>
              {showDetails ? (
                <Button
                  variant="outline"
                  onClick={() => persist({ analytics, marketing })}
                >
                  Gem mine valg
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setShowDetails(true)}>
                  Tilpas
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => persist({ analytics: false, marketing: false })}
              >
                Kun nødvendige
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
